import React, { useEffect, useState, useRef } from 'react';
import { Col, Row } from 'react-bootstrap';
import GaugeChart from 'src/components/Dashboard/AdminDashboard/GaugeChart';
import VehicleTrackingStationOverview from 'src/components/Dashboard/AdminDashboard/VehicleTrackingStationOverview';
import { ReactComponent as TotalVehicleIcon } from 'src/Images/icons/common/totalVehicleIcon.svg';
import { ReactComponent as TotalWorkFlowIcon } from 'src/Images/icons/common/TotalWorkFlowIcon.svg';
import { ReactComponent as IncomingVehicleIcon } from 'src/Images/icons/common/incomingVehicleIcon.svg';
import { ReactComponent as CurrentVehicleIcon } from 'src/Images/icons/common/currentVehicleIcon.svg';
import { ReactComponent as DelayedVehicleIcon } from 'src/Images/icons/common/delayedVehicleIcon.svg';
import { ReactComponent as CompletedVehicleIcon } from 'src/Images/icons/common/completedVehicleIcon.svg';
import TruckXs from 'src/Images/icons/truck/truck-xs.png';
import TruckSm from 'src/Images/icons/truck/truck-sm.png';
import TruckMd from 'src/Images/icons/truck/truck-md.png';
import TruckLg from 'src/Images/icons/truck/truck-lg.png';
import TruckXl from 'src/Images/icons/truck/truck-xl.png';
import 'src/style/overallDashboard.css';
import { useDispatch, useSelector } from 'react-redux';
import getColorCode from 'src/utils/GetThemeColor';
import {
  getVehicleTrackingDashboardData,
  getVehicleTrackingWorkflows,
  getWorkflowStagesForCurrentUser,
} from 'src/redux/Actions/VehicleTracking';
import VehicleTrackingStationMap from 'src/components/Dashboard/AdminDashboard/VehicleTrackingStationMap';
import { VehicleProgress } from 'src/components/Dashboard/AdminDashboard/VehicleProgressComponent';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import { ReactComponent as DropdownIcon } from 'src/Images/icons/common/dropdown.svg';
import { ReactComponent as CompletedVehicles } from 'src/Images/icons/EmptyStates/completedVehicles.svg';
import { ReactComponent as EmptyMap } from 'src/Images/icons/EmptyStates/emptyMap.svg';
import { ReactComponent as QualityCheck } from 'src/Images/icons/EmptyStates/qualitycheck.svg';
import { ReactComponent as StationOverviewEmpty } from 'src/Images/icons/EmptyStates/stationOverviewEmpty.svg';
import { ReactComponent as IncomingVehicle } from 'src/Images/icons/common/incomingMapVehicle.svg';
import { ReactComponent as CurrentVehicle } from 'src/Images/icons/common/currentVehicle.svg';
import { ReactComponent as VehiclesDelayedForStationComponent } from 'src/Images/icons/common/vehiclesDelayedForStationComponent.svg';
import { useNavigate } from 'react-router-dom';
import LinearProgress from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import { useMediaQuery } from '@mui/system';
import VehicleMobileTabs from 'src/components/Dashboard/UserDashboard/VehicleMobileTabs';
import VehicleTypeListForDashboard from 'src/components/Dashboard/UserDashboard/VehicleTypeListForDashboard';
import { usePermissions } from 'src/utils/hooks/usePermissions';
import { ACTIONS } from 'src/utils/roles/actions';

export function OverallDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  const authReducer = useSelector((state) => state.AuthReducer);
  const organisationId = authReducer.userData.organisations?.id;
  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantId = authReducer.userData.tenant?.id;
  const vehicleTrackingReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const { organisationAdmin } = authReducer;

  // Use centralized permission hook
  const { isPermissionsLoaded, hasPermission } = usePermissions();

  // Compute permission flags
  const hasInwardPermission =
    hasPermission(ACTIONS.VEHICLE_TRACKING.INWARD.VIEW) ||
    hasPermission(ACTIONS.VEHICLE_TRACKING.INWARD.CREATE);
  const hasOutwardPermission =
    hasPermission(ACTIONS.VEHICLE_TRACKING.OUTWARD.VIEW) ||
    hasPermission(ACTIONS.VEHICLE_TRACKING.OUTWARD.CREATE);

  const { workflowStagesForcurrentUser } = vehicleTrackingReducer;
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [selectedWorkflowStageId, setSelectedWorkflowStageId] = useState(null);
  const defaultDate = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(defaultDate);
  const [endDate, setEndDate] = useState(defaultDate);

  const rawDashboardResponse =
    vehicleTrackingReducer?.vehicleTrackingDashboardData?.responseData || {};
  const workflowsFromSeparateReducer = vehicleTrackingReducer?.workflows || [];
  const workflowSelectOptions = workflowsFromSeparateReducer.map((w) => ({
    value: w.id,
    label: w.name,
  }));
  const workflowsInResponse = rawDashboardResponse?.workflows || [];

  const findActiveWorkflow = () => {
    const byResponse = workflowsInResponse.find(
      (w) => w.id === selectedWorkflowId,
    );
    if (byResponse) return byResponse;
    if (workflowsInResponse.length > 0) return workflowsInResponse[0];
    return null;
  };

  const activeWorkflow = findActiveWorkflow();
  const workflowStageMetrics = activeWorkflow?.workflow_stages || [];

  const stationOverviewData = workflowStageMetrics.map((stage) => {
    const grouped = stage?.grouped_vehicle_tracking || {};

    const incomingArray =
      grouped.incoming_vehicle_trackings?.vehicle_tracking ?? [];

    const filteredIncomingArray = incomingArray;

    return {
      station: stage?.name || '',
      incoming: filteredIncomingArray.length,
      current: stage.total_current_count,
      delayed: stage.total_delayed_count,
    };
  });

  const hasStationData =
    workflowStageMetrics.length > 0 &&
    stationOverviewData.some(
      (station) =>
        station.incoming > 0 || station.current > 0 || station.delayed > 0,
    );

  const stationMapData = workflowStageMetrics.map((stage) => {
    const grouped = stage?.grouped_vehicle_tracking || {};

    const vehicleTrackingKey = (vehicle) =>
      vehicle?.vehicle_tracking_number || vehicle?.vehicle_tracking_code;

    const delayedArray =
      grouped.delayed_vehicle_trackings?.vehicle_tracking ?? [];
    const incomingArray =
      grouped.incoming_vehicle_trackings?.vehicle_tracking ?? [];

    const delayedKeys = new Set(
      delayedArray.map((vehicle) => vehicleTrackingKey(vehicle)),
    );

    const filteredIncomingArray = incomingArray.filter(
      (vehicle) => !delayedKeys.has(vehicleTrackingKey(vehicle)),
    );

    const delayedDetail = grouped.delayed_vehicle_trackings || {};

    const incomingDetail = {
      ...(grouped.incoming_vehicle_trackings || {}),
      vehicle_tracking: filteredIncomingArray,
      count: filteredIncomingArray.length,
    };

    const currentDetail = grouped.current_vehicle_trackings || {};

    return {
      station_id: stage?.workflow_stage_id,
      station_uuid: stage?.uuid,
      station: stage?.name,
      incoming: filteredIncomingArray.length,
      current: stage.total_current_count,
      delayed: stage.total_delayed_count,
      delayed_detail: delayedDetail,
      incoming_detail: incomingDetail,
      current_detail: currentDetail,
    };
  });

  const hasStationMapData = stationOverviewData.some(
    (station) =>
      Number(station.incoming || 0) > 0 || Number(station.current || 0) > 0,
  );

  const extractCompletedFromStage = (stage) => {
    const group = stage?.grouped_vehicle_tracking || {};
    const completed =
      group?.completed_vehicle_trackings?.vehicle_tracking || [];
    return Array.isArray(completed) ? completed : [];
  };

  const getAllCompletedVehicles = (workflowsArray) => {
    const all = [];
    (workflowsArray || []).forEach((wf) => {
      const stages = wf?.workflow_stages || [];
      stages.forEach((st) => {
        const completedList = extractCompletedFromStage(st);
        if (completedList?.length) all.push(...completedList);
      });
    });

    const dedupe = {};
    const unique = [];

    all.forEach((vehicle) => {
      const key =
        vehicle?.vehicle_tracking_code ||
        vehicle?.vehicle_tracking_number ||
        vehicle?.id;
      if (!key) return;
      if (!dedupe[key]) {
        dedupe[key] = true;
        unique.push(vehicle);
      }
    });
    return unique;
  };

  const completedVehicleList = getAllCompletedVehicles(workflowsInResponse);

  const percentOf = (value, total) =>
    total > 0 ? Math.round((value / total) * 100) : 0;

  const stageCounts = {
    incomingPercentage: percentOf(
      rawDashboardResponse.total_incoming_count,
      rawDashboardResponse.total_count,
    ),

    currentPercentage: percentOf(
      rawDashboardResponse.total_current_count,
      rawDashboardResponse.total_count,
    ),

    completedPercentage: percentOf(
      rawDashboardResponse.total_completed_count,
      rawDashboardResponse.total_count,
    ),

    incomingVehicleCount: rawDashboardResponse.total_incoming_count,
    currentVehicleCount: rawDashboardResponse.total_current_count,
    completedVehicleCount: rawDashboardResponse.total_completed_count,

    completedVehicleDetails: completedVehicleList,
  };

  const dashboardSummary = {
    total_organisation_count: rawDashboardResponse.total_organisation_count,
    totalUserCount: rawDashboardResponse.total_user_count,
    totalWorkflowStagesCount: workflowStageMetrics.length,
    totalWorkflowsCount:
      workflowsFromSeparateReducer.length || workflowsInResponse.length,
    totalProductsCount: rawDashboardResponse.total_product_count,
    totalPackTypesCount: rawDashboardResponse.total_packtype_count,
    totalQualityChecksCount: rawDashboardResponse.total_quality_check,
    qualityCheckCounts: {
      rejected: rawDashboardResponse.quality_check_rejected,
      open: rawDashboardResponse.quality_check_open,
      approved: rawDashboardResponse.quality_check_approved,
    },
  };

  const getWorkflowStageOptions = () => {
    if (!organisationAdmin) {
      return workflowStagesForcurrentUser.map((workflowObj) => ({
        label: workflowObj.name,
        value: workflowObj.id,
      }));
    }

    return [
      { label: 'Select Workflow Stage', value: null },
      ...workflowStagesForcurrentUser.map((workflowObj) => ({
        label: workflowObj.name,
        value: workflowObj.id,
      })),
    ];
  };
  const workflowOptions = getWorkflowStageOptions();

  useEffect(() => {
    if (!organisationAdmin && workflowStagesForcurrentUser.length > 0) {
      setSelectedWorkflowStageId(workflowStagesForcurrentUser[0].id);
    }
  }, [organisationAdmin, workflowStagesForcurrentUser]);

  useEffect(() => {
    // Skip dashboard API call if a user doesn't have a workflow stage selected on first load
    if (!organisationAdmin && !selectedWorkflowStageId) return;
    // Wait for workflow ID to be set
    if (selectedWorkflowId === null) return;

    dispatch(
      getVehicleTrackingDashboardData(
        organisationId,
        startDate,
        endDate,
        selectedWorkflowId,
        selectedWorkflowStageId,
      ),
    );
  }, [
    organisationId,
    startDate,
    endDate,
    selectedWorkflowId,
    selectedWorkflowStageId,
    organisationAdmin,
    dispatch,
  ]);

  useEffect(() => {
    if (isPermissionsLoaded && organisationId) {
      // Determine direction based on permissions
      let direction = null;
      if (hasInwardPermission && !hasOutwardPermission) {
        direction = 1;
      } else if (!hasInwardPermission && hasOutwardPermission) {
        direction = 2;
      }
      dispatch(getVehicleTrackingWorkflows(organisationId, direction));
    }
  }, [
    dispatch,
    tenantId,
    organisationId,
    hasInwardPermission,
    hasOutwardPermission,
    isPermissionsLoaded,
  ]);

  // Set default workflow ID once workflows are loaded
  useEffect(() => {
    if (
      isPermissionsLoaded &&
      selectedWorkflowId === null &&
      workflowsFromSeparateReducer.length > 0
    ) {
      setSelectedWorkflowId(workflowsFromSeparateReducer[0].id);
    }
  }, [isPermissionsLoaded, selectedWorkflowId, workflowsFromSeparateReducer]);

  useEffect(() => {
    if (isPermissionsLoaded && selectedWorkflowId !== null) {
      dispatch(
        getWorkflowStagesForCurrentUser(organisationId, selectedWorkflowId),
      );
    }
  }, [dispatch, organisationId, selectedWorkflowId, isPermissionsLoaded]);

  const hasQualityCheckData =
    dashboardSummary.qualityCheckCounts?.rejected ||
    dashboardSummary.qualityCheckCounts?.open ||
    dashboardSummary.qualityCheckCounts?.approved;

  const BorderLinearProgress = styled(LinearProgress)(() => ({
    height: 6,
    borderRadius: 50,
    backgroundColor: '#E3F3E8',
    '& .MuiLinearProgress-bar': {
      backgroundColor: '#0E9A26',
      borderRadius: 50,
    },
  }));

  const incomingScrollRef = useRef(null);
  const currentScrollRef = useRef(null);
  const delayedScrollRef = useRef(null);

  const scrollLeft = (ref) => {
    if (ref?.current) {
      ref.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };
  const scrollRight = (ref) => {
    if (ref?.current) {
      ref.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const mergedVehicleTypes = workflowStageMetrics.reduce((acc, stage) => {
    const types = stage?.vehicle_assigned_by_type || {};

    Object.entries(types).forEach(([typeKey, typeData]) => {
      if (!acc[typeKey]) {
        acc[typeKey] = {
          name: typeData.name,
          total: 0,
          incoming: 0,
          current: 0,
          completed: 0,
          delayed: 0,
        };
      }

      acc[typeKey].total += typeData.total || 0;
      acc[typeKey].incoming += typeData.incoming || 0;
      acc[typeKey].current += typeData.current || 0;
      acc[typeKey].completed += typeData.completed || 0;
      acc[typeKey].delayed += typeData.delayed || 0;
    });

    return acc;
  }, {});

  // Find selected stage
  const selectedStage = workflowStageMetrics.find(
    (stage) => stage.workflow_stage_id === selectedWorkflowStageId,
  );

  const grouped = selectedStage?.grouped_vehicle_tracking || {};

  // Helper to keep previous data
  const mapVehicles = (list = []) =>
    list.map((vehicle) => ({
      ...vehicle,
      current_stage: vehicle?.current_stage,
      previous_stage: vehicle?.previous_stage,
      workflow_stage_name: vehicle?.workflow_stage_name,
    }));

  // Incoming vehicles only from selected stage
  const allIncomingVehicles = mapVehicles(
    grouped?.incoming_vehicle_trackings?.vehicle_tracking || [],
  );

  // Current vehicles only from selected stage
  const allCurrentVehicles = mapVehicles(
    grouped?.current_vehicle_trackings?.vehicle_tracking || [],
  );

  // Delayed vehicles only from selected stage
  const allDelayedVehicles = mapVehicles(
    grouped?.delayed_vehicle_trackings?.vehicle_tracking || [],
  );

  const convertToMinutes = (timeStr) => {
    if (!timeStr) return null;

    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':');

    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10);

    if (modifier === 'PM' && hours !== 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;

    return hours * 60 + minutes;
  };

  const getProgressPercent = (prevExit, currEstimate) => {
    const prevMin = convertToMinutes(prevExit);
    const currMin = convertToMinutes(currEstimate);

    if (prevMin === null || currMin === null) return 0;
    if (currMin <= prevMin) return 100; // avoid negative or zero gap

    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();

    if (nowMinutes <= prevMin) return 0;
    if (nowMinutes >= currMin) return 100;

    const percent = ((nowMinutes - prevMin) / (currMin - prevMin)) * 100;
    return Math.min(100, Math.max(0, percent));
  };
  const [vehicleTab, setVehicleTab] = useState(0);

  return (
    <div className="page-layout">
      <div
        className={`dashboard-container theme-vertical-scrollable-container-${themeColor}`}
      >
        <Row className="padding-bottom-10 div-space-between div-align-center horizontal-padding-10">
          <Col xxl={4} xl={4} lg={5}>
            <div className="div-flex-column">
              <span className="text-xl text-bold">
                Hello, {organisationAdmin ? 'Admin' : 'User'}
              </span>
              <span className="text-sm text-grey">
                Welcome back! Let&apos;s get things done.
              </span>
            </div>
          </Col>

          <Col xxl={6} xl={6} lg={6}>
            <Row className="g-2 div-flex-end">
              <Col xxl={3} xl={3} lg={3} md={6} sm={6} xs={6}>
                <div className="input-wrapper">
                  <CustomMultiSelect
                    name="workflowId"
                    isMulti={false}
                    className="multi-select-input-section"
                    placeholder="Select Workflow"
                    onChange={(selectedOption) => {
                      setSelectedWorkflowId(selectedOption?.value || null);
                      setSelectedWorkflowStageId(null);
                    }}
                    value={workflowSelectOptions.find(
                      (option) => option.value === selectedWorkflowId,
                    )}
                    options={workflowSelectOptions}
                  />
                  <span className="form-input-section-icon">
                    <DropdownIcon
                      className="icon-size-sm"
                      color={getColorCode(themeColor)}
                    />
                  </span>
                </div>
              </Col>
              <Col xxl={3} xl={3} lg={3} md={6} sm={6} xs={6}>
                <div className="input-wrapper">
                  <CustomMultiSelect
                    name="workflowId"
                    isMulti={false}
                    className="multi-select-input-section"
                    onChange={(selectedOption) => {
                      setSelectedWorkflowStageId(selectedOption?.value || null);
                    }}
                    value={workflowOptions.find(
                      (option) => option.value === selectedWorkflowStageId,
                    )}
                    options={workflowOptions}
                  />
                  <span className="form-input-section-icon">
                    <DropdownIcon
                      className="icon-size-sm"
                      color={getColorCode(themeColor)}
                    />
                  </span>
                </div>
              </Col>
              <Col xxl={3} xl={3} lg={3} md={6} sm={6} xs={6}>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-input-section"
                  aria-label="start-date"
                />
              </Col>
              <Col xxl={3} xl={3} lg={3} md={6} sm={6} xs={6}>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="form-input-section"
                  aria-label="end-date"
                />
              </Col>
            </Row>
          </Col>
        </Row>

        <div className="border-bottom" />

        <div className="div-flex-column rg-10 vertical-padding-10 horizontal-padding-10">
          <Row className="g-2">
            <Col xxl={2} xl={4} lg={4} md={4} sm={4} xs={12}>
              <div className="dashboard-card div-flex-row div-space-between div-align-center">
                <div className="div-flex-row cg-5 div-align-center">
                  <TotalWorkFlowIcon className="icon-size-xxl" />
                  <span className="text-lg text-semi-bold">Total Workflow</span>
                </div>
                <div className="div-flex-row div-space-between div-align-center">
                  <span className="text-xxl text-bold">
                    {dashboardSummary.totalWorkflowsCount || '00'}
                  </span>
                </div>
              </div>
            </Col>
            <Col xxl={2} xl={4} lg={4} md={4} sm={4} xs={12}>
              <div className="dashboard-card div-flex-row div-space-between div-align-center">
                <div className="div-flex-row cg-5 div-align-center">
                  <TotalVehicleIcon className="vehicle-icon-size" />
                  <span className="text-lg text-semi-bold">Total Vehicles</span>
                </div>
                <div className="div-flex-row div-space-between div-align-center">
                  <span className="text-xxl text-bold">
                    {rawDashboardResponse.total_count || '00'}
                  </span>
                </div>
              </div>
            </Col>
            <Col xxl={2} xl={4} lg={4} md={4} sm={4} xs={12}>
              <div className="dashboard-card div-flex-row div-space-between div-align-center">
                <div className="div-flex-row cg-5 div-align-center">
                  <IncomingVehicleIcon className="vehicle-icon-size" />
                  <span className="text-lg text-semi-bold">
                    Incoming Vehicles
                  </span>
                </div>
                <div className="div-flex-row div-space-between div-align-center">
                  <span className="text-xxl text-bold">
                    {rawDashboardResponse.total_incoming_count || '00'}
                  </span>
                </div>
              </div>
            </Col>
            <Col xxl={2} xl={4} lg={4} md={4} sm={4} xs={12}>
              <div className="dashboard-card div-flex-row div-space-between div-align-center">
                <div className="div-flex-row cg-5 div-align-center">
                  <DelayedVehicleIcon className="vehicle-icon-size" />
                  <span className="text-lg text-semi-bold">
                    Delayed Vehicles
                  </span>
                </div>
                <div className="div-flex-row div-space-between div-align-center">
                  <span className="text-xxl text-bold">
                    {rawDashboardResponse.total_delayed_count || '00'}
                  </span>
                </div>
              </div>
            </Col>
            <Col xxl={2} xl={4} lg={4} md={4} sm={4} xs={12}>
              <div className="dashboard-card div-flex-row div-space-between div-align-center">
                <div className="div-flex-row cg-5 div-align-center">
                  <CurrentVehicleIcon className="vehicle-icon-size" />
                  <span className="text-lg text-semi-bold">
                    Current Vehicles
                  </span>
                </div>
                <div className="div-flex-row div-space-between div-align-center">
                  <span className="text-xxl text-bold">
                    {rawDashboardResponse.total_current_count || '00'}
                  </span>
                </div>
              </div>
            </Col>
            <Col xxl={2} xl={4} lg={4} md={4} sm={4} xs={12}>
              <div className="dashboard-card div-flex-row div-space-between div-align-center">
                <div className="div-flex-row cg-5 div-align-center">
                  <CompletedVehicleIcon className="vehicle-icon-size" />
                  <span className="text-lg text-semi-bold">
                    Total Completed
                  </span>
                </div>
                <div className="div-flex-row div-space-between div-align-center">
                  <span className="text-xxl text-bold">
                    {rawDashboardResponse.total_completed_count || '00'}
                  </span>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="g-2">
            <Col
              xxl={organisationAdmin ? 10 : 12}
              xl={organisationAdmin ? 8 : 12}
              lg={organisationAdmin ? 8 : 12}
            >
              <div className="div-flex-column rg-5 vertical-padding-10 horizontal-padding-10 border border-radius-4">
                <Row className="div-flex-row div-space-between div-align-center">
                  <Col xxl={8} xl={8} lg={8}>
                    <div className="div-flex-column">
                      <span className="text-lg text-semi-bold">
                        Station Overview
                      </span>
                      <span className="text-sm text-grey">
                        Overview of your vehicle at each station along with its
                        status.
                      </span>
                    </div>
                  </Col>
                  <Col xxl={4} xl={4} lg={4}>
                    <div className="div-flex-row div-flex-end mt-1 cg-20">
                      <div className="div-flex-row cg-5 div-align-center">
                        <div className="incoming-color-bar" />
                        <span className="text-sm text-grey">Incoming</span>
                      </div>
                      <div className="div-flex-row cg-5 div-align-center">
                        <div className="current-color-bar" />
                        <span className="text-sm text-grey">Current</span>
                      </div>
                      <div className="div-flex-row cg-5 div-align-center">
                        <div className="delayed-color-bar" />
                        <span className="text-sm text-grey">Delayed</span>
                      </div>
                    </div>
                  </Col>
                </Row>
                {hasStationData ? (
                  <div className="station-overview-height">
                    <VehicleTrackingStationOverview
                      data={stationOverviewData}
                    />
                  </div>
                ) : (
                  <div className="div-flex-column station-overview-height rg-10 div-flex-center div-align-center">
                    <StationOverviewEmpty />
                    <span className="text-sm text-grey">
                      No station overview data available.
                    </span>
                  </div>
                )}
              </div>
            </Col>
            {organisationAdmin && (
              <Col xxl={2} xl={4} lg={4}>
                <div className="div-flex-column rg-10">
                  <div className="div-flex-column rg-10 vertical-padding-10 horizontal-padding-10 border border-radius-4">
                    <div className="div-flex-column">
                      <span className="text-lg text-bold">Quality Check</span>
                      <span className="text-sm text-grey">
                        Analytic view of quality check.
                      </span>
                    </div>

                    {hasQualityCheckData ? (
                      <div className="div-flex-column quality-check-height rg-10 div-align-center py-5">
                        <GaugeChart
                          rejected={
                            dashboardSummary.qualityCheckCounts?.rejected
                          }
                          open={dashboardSummary.qualityCheckCounts?.open}
                          approved={
                            dashboardSummary.qualityCheckCounts?.approved
                          }
                        />
                        <span className="text-sm text-grey">
                          Quality check assigned to vehicle
                        </span>
                      </div>
                    ) : (
                      <div className="div-flex-column quality-check-error-height div-flex-center div-align-center rg-10">
                        <QualityCheck />
                        <span className="text-sm text-grey">
                          No quality check data.
                        </span>
                      </div>
                    )}

                    {hasQualityCheckData && (
                      <div className="div-flex-column rg-10">
                        <div className="div-flex-row div-space-between div-align-center">
                          <div className="div-flex-row cg-5 div-align-center">
                            <div className="rejected-color-bar" />
                            <span className="text-sm text-grey">Rejected</span>
                          </div>
                          <div className="rejected-text-container">
                            <span className="text-sm text-semi-bold">
                              {dashboardSummary.qualityCheckCounts?.rejected}
                            </span>
                          </div>
                        </div>

                        <div className="div-flex-row div-space-between div-align-center">
                          <div className="div-flex-row cg-5 div-align-center">
                            <div className="open-color-bar" />
                            <span className="text-sm text-grey">Open</span>
                          </div>
                          <div className="open-text-container">
                            <span className="text-sm text-semi-bold">
                              {dashboardSummary.qualityCheckCounts?.open}
                            </span>
                          </div>
                        </div>

                        <div className="div-flex-row div-space-between div-align-center">
                          <div className="div-flex-row cg-5 div-align-center">
                            <div className="approved-color-bar" />
                            <span className="text-sm text-grey">Approved</span>
                          </div>
                          <div className="approved-text-container">
                            <span className="text-sm text-semi-bold">
                              {dashboardSummary.qualityCheckCounts?.approved}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            )}
          </Row>
          {/* Incoming */}
          {!isMobile && !organisationAdmin && (
            <div className="div-flex-column rg-5 vertical-padding-10 horizontal-padding-10 border border-radius-4">
              <div className="div-flex-row div-space-between div-align-center">
                <span className="text-lg text-bold">Incoming Vehicles</span>
              </div>

              <VehicleTypeListForDashboard
                vehicles={allIncomingVehicles}
                scrollRef={incomingScrollRef}
                getProgress={getProgressPercent}
                isMobile={isMobile}
                themeColor={themeColor}
                BorderLinearProgress={BorderLinearProgress}
                StationOverviewEmpty={StationOverviewEmpty}
                scrollLeft={scrollLeft}
                scrollRight={scrollRight}
                selectedWorkflowStageId={selectedWorkflowStageId}
                containerClass="incoming-vehicle-card-wrapper"
                stageFrom="previous_stage"
                stageTo="current_stage"
                timeFrom="exit_time"
                timeTo="estimated_entry_time"
                emptyMessage="No Incoming Vehicles."
                statusLabel="On time"
              />
            </div>
          )}
          {/* Current */}
          {!isMobile && !organisationAdmin && (
            <div className="div-flex-column rg-5 vertical-padding-10 horizontal-padding-10 border border-radius-4">
              <div className="div-flex-row div-space-between div-align-center">
                <span className="text-lg text-bold">Current Vehicles</span>
              </div>

              <VehicleTypeListForDashboard
                vehicles={allCurrentVehicles}
                scrollRef={currentScrollRef}
                getProgress={getProgressPercent}
                isMobile={isMobile}
                themeColor={themeColor}
                BorderLinearProgress={BorderLinearProgress}
                StationOverviewEmpty={StationOverviewEmpty}
                scrollLeft={scrollLeft}
                scrollRight={scrollRight}
                selectedWorkflowStageId={selectedWorkflowStageId}
                containerClass="current-vehicle-card-container"
                stageFrom="current_stage"
                stageTo="next_stage"
                timeFrom="entry_time"
                timeTo="estimated_entry_time"
                emptyMessage="No Current Vehicles."
                statusLabel="In progress"
              />
            </div>
          )}
          {/* Delayed */}
          {!isMobile && !organisationAdmin && (
            <div className="div-flex-column rg-5 vertical-padding-10 horizontal-padding-10 border border-radius-4">
              <div className="div-flex-row div-space-between div-align-center">
                <span className="text-lg text-bold">Delayed Vehicles</span>
              </div>

              <VehicleTypeListForDashboard
                vehicles={allDelayedVehicles}
                scrollRef={delayedScrollRef}
                getProgress={getProgressPercent}
                isMobile={isMobile}
                themeColor={themeColor}
                BorderLinearProgress={BorderLinearProgress}
                StationOverviewEmpty={StationOverviewEmpty}
                scrollLeft={scrollLeft}
                scrollRight={scrollRight}
                selectedWorkflowStageId={selectedWorkflowStageId}
                containerClass="delayed-vehicle-card-container"
                stageFrom="previous_stage"
                stageTo="current_stage"
                timeFrom="exit_time"
                timeTo="estimated_entry_time"
                emptyMessage="No Delayed Vehicles."
                statusLabel="Delayed"
              />
            </div>
          )}

          {isMobile && !organisationAdmin && (
            <VehicleMobileTabs
              activeTab={vehicleTab}
              setActiveTab={setVehicleTab}
              themeColor={themeColor}
              tabContent={[
                {
                  title: 'Incoming',
                  count: allIncomingVehicles.length,
                  borderColor: 'border-bottom-yellow',
                  textColor: 'text-yellow',
                  content: (
                    <VehicleTypeListForDashboard
                      vehicles={allIncomingVehicles}
                      scrollRef={incomingScrollRef}
                      getProgress={getProgressPercent}
                      isMobile={isMobile}
                      themeColor={themeColor}
                      BorderLinearProgress={BorderLinearProgress}
                      StationOverviewEmpty={StationOverviewEmpty}
                      scrollLeft={scrollLeft}
                      scrollRight={scrollRight}
                      selectedWorkflowStageId={selectedWorkflowStageId}
                      containerClass="incoming-vehicle-card-wrapper"
                      stageFrom="previous_stage"
                      stageTo="current_stage"
                      timeFrom="exit_time"
                      timeTo="estimated_entry_time"
                      emptyMessage="No Incoming Vehicles."
                      statusLabel="On time"
                    />
                  ),
                },
                {
                  title: 'Current',
                  count: allCurrentVehicles.length,
                  borderColor: 'border-bottom-green',
                  textColor: 'text-green',
                  content: (
                    <VehicleTypeListForDashboard
                      vehicles={allCurrentVehicles}
                      scrollRef={currentScrollRef}
                      getProgress={getProgressPercent}
                      isMobile={isMobile}
                      themeColor={themeColor}
                      BorderLinearProgress={BorderLinearProgress}
                      StationOverviewEmpty={StationOverviewEmpty}
                      scrollLeft={scrollLeft}
                      scrollRight={scrollRight}
                      selectedWorkflowStageId={selectedWorkflowStageId}
                      containerClass="current-vehicle-card-container"
                      stageFrom="current_stage"
                      stageTo="next_stage"
                      timeFrom="entry_time"
                      timeTo="estimated_entry_time"
                      emptyMessage="No Current Vehicles."
                      statusLabel="In progress"
                    />
                  ),
                },
                {
                  title: 'Delayed',
                  count: allDelayedVehicles.length,
                  borderColor: 'border-bottom-red',
                  textColor: 'text-red',
                  content: (
                    <VehicleTypeListForDashboard
                      vehicles={allDelayedVehicles}
                      scrollRef={delayedScrollRef}
                      getProgress={getProgressPercent}
                      isMobile={isMobile}
                      themeColor={themeColor}
                      BorderLinearProgress={BorderLinearProgress}
                      StationOverviewEmpty={StationOverviewEmpty}
                      scrollLeft={scrollLeft}
                      scrollRight={scrollRight}
                      selectedWorkflowStageId={selectedWorkflowStageId}
                      containerClass="delayed-vehicle-card-container"
                      stageFrom="previous_stage"
                      stageTo="current_stage"
                      timeFrom="exit_time"
                      timeTo="estimated_entry_time"
                      emptyMessage="No Delayed Vehicles."
                      statusLabel="Delayed"
                    />
                  ),
                },
              ]}
            />
          )}

          {!organisationAdmin && (
            <div className="div-flex-column vertical-padding-10 horizontal-padding-10 border border-radius-4">
              <div className="div-flex-row div-space-between div-align-center">
                <span className="text-lg text-bold">
                  Vehicles Assigned by Type
                </span>
              </div>

              <div
                className={
                  isMobile
                    ? `div-flex-column rg-10`
                    : `div-flex-row cg-20 theme-horizontal-scrollable-container-${themeColor}`
                }
              >
                {Object.entries(mergedVehicleTypes).map(([key, typeObj]) => {
                  const imageMap = {
                    1: TruckXs,
                    2: TruckSm,
                    3: TruckMd,
                    4: TruckLg,
                    5: TruckXl,
                  };

                  const truckImage = imageMap[key];

                  return (
                    <div className="w-100" key={key}>
                      <div
                        className={`completed-vehicle-card ${isMobile ? '' : 'border-bottom'}`}
                      >
                        <img
                          src={truckImage}
                          alt={typeObj.name}
                          className="completed-vehicle-image"
                        />

                        <div className="complete-vehicle-code-blue">
                          <span className="text-sm text-semi-bold">
                            {typeObj.name}
                          </span>
                        </div>

                        <div className="completed-vehicle-card-container div-flex-column rg-10">
                          <div className="div-flex-row cg-5 text-semi-bold div-flex-end mt-2 div-align-center">
                            <span className="text-xl">{typeObj.total}</span>
                            <span className="text-sm text-grey">
                              {' '}
                              Total Vehicles
                            </span>
                          </div>

                          <Row className="g-1">
                            <Col xs={6}>
                              <div className="div-flex-column">
                                <span className="text-sm text-semi-bold text-grey">
                                  Incoming
                                </span>
                                <span className="text-sm text-semi-bold">
                                  {typeObj.incoming}
                                </span>
                              </div>
                            </Col>

                            <Col xs={6}>
                              <div className="div-flex-column">
                                <span className="text-sm text-semi-bold text-grey">
                                  Current
                                </span>
                                <span className="text-sm text-semi-bold">
                                  {typeObj.current}
                                </span>
                              </div>
                            </Col>

                            <Col xs={6}>
                              <div className="div-flex-column">
                                <span className="text-sm text-semi-bold text-grey">
                                  Completed
                                </span>
                                <span className="text-sm text-semi-bold">
                                  {typeObj.completed}
                                </span>
                              </div>
                            </Col>

                            <Col xs={6}>
                              <div className="div-flex-column">
                                <span className="text-sm text-semi-bold text-grey">
                                  Delayed
                                </span>
                                <span className="text-sm text-semi-bold">
                                  {typeObj.delayed}
                                </span>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Row className="g-2">
            <Col xxl={2} xl={3} lg={4} md={6} sm={12} xs={12}>
              <div className="div-flex-column rg-10">
                <div className="div-flex-column rg-20 vertical-padding-10 horizontal-padding-10 border border-radius-4">
                  <div className="div-flex-column">
                    <span className="text-lg text-semi-bold">
                      Incoming Vehicle
                    </span>
                    <span className="text-sm text-grey">
                      Total value of Incoming vehicle.
                    </span>
                  </div>
                  <VehicleProgress
                    percent={stageCounts.incomingPercentage}
                    theme="blue"
                  />
                  <div className="incoming-vehicle-progress">
                    <span className="text-sm text-semi-bold">
                      {stageCounts.incomingVehicleCount}
                    </span>
                  </div>
                </div>

                <div className="div-flex-column rg-20 vertical-padding-10 horizontal-padding-10 border border-radius-4">
                  <div className="div-flex-column">
                    <span className="text-lg text-semi-bold">
                      Vehicle In Process
                    </span>
                    <span className="text-sm text-grey">
                      Total value of in process vehicle.
                    </span>
                  </div>
                  <VehicleProgress
                    percent={stageCounts.currentPercentage}
                    theme="orange"
                  />
                  <div className="in-progress-vehicle-progress">
                    <span className="text-sm text-semi-bold">
                      {stageCounts.currentVehicleCount}
                    </span>
                  </div>
                </div>

                <div className="div-flex-column rg-20 vertical-padding-10 horizontal-padding-10 border border-radius-4">
                  <div className="div-flex-column">
                    <span className="text-lg text-semi-bold">
                      Completed Vehicle
                    </span>
                    <span className="text-sm text-grey">
                      Total value of completed vehicle.
                    </span>
                  </div>
                  <VehicleProgress
                    percent={stageCounts.completedPercentage}
                    theme="green"
                  />
                  <div className="completed-vehicle-progress">
                    <span className="text-sm text-semi-bold">
                      {stageCounts.completedVehicleCount}
                    </span>
                  </div>
                </div>
              </div>
            </Col>
            <Col xxl={10} xl={9} lg={8}>
              <div className="station-tracking-container div-flex-column">
                <div
                  className={`${isMobile ? 'div-flex-column rg-10' : 'div-flex-row div-space-between'} horizontal-padding-10 vertical-padding-10`}
                >
                  <div className="div-flex-column">
                    <span className="text-lg text-semi-bold">
                      Station Tracking
                    </span>
                    <span className="text-sm text-grey">
                      Keep a live track of your vehicle at each station.
                    </span>
                  </div>
                  <div className="div-flex-row cg-10">
                    <div className="div-flex-row div-align-center">
                      <VehiclesDelayedForStationComponent className="icon-size-big" />
                      <span className="text-lg text-semi-bold text-nowrap">
                        Delayed
                      </span>
                    </div>
                    <div className="div-flex-row div-align-center">
                      <IncomingVehicle className="icon-size-big" />
                      <span className="text-lg text-semi-bold text-nowrap">
                        Incoming
                      </span>
                    </div>
                    <div className="div-flex-row div-align-center">
                      <CurrentVehicle
                        transform="rotate(50)"
                        className="icon-size-big"
                      />
                      <span className="text-lg text-semi-bold text-nowrap">
                        Current
                      </span>
                    </div>
                  </div>
                </div>
                {hasStationMapData ? (
                  <VehicleTrackingStationMap data={stationMapData} />
                ) : (
                  <div className="div-flex-column map-height div-flex-center div-align-center rg-10">
                    <EmptyMap />
                    <span className="text-sm text-grey">
                      No vehicle data to map.
                    </span>
                  </div>
                )}
              </div>
            </Col>
          </Row>
          {organisationAdmin && (
            <div className="div-flex-column rg-5 vertical-padding-10 horizontal-padding-10 border border-radius-4">
              <span className="text-lg text-bold">Completed Vehicles</span>
              {stageCounts.completedVehicleDetails.length > 0 ? (
                <div
                  className={
                    isMobile
                      ? `div-flex-column rg-10 complete-vehicle-container-mobile-height theme-vertical-scrollable-container-${themeColor}`
                      : `div-flex-row cg-20 theme-horizontal-scrollable-container-${themeColor}`
                  }
                >
                  {stageCounts.completedVehicleDetails?.map((vehicleObj) => (
                    <Col
                      xxl={2}
                      xl={3}
                      lg={4}
                      key={
                        vehicleObj?.vehicle_tracking_code ||
                        vehicleObj?.vehicle_tracking_number ||
                        vehicleObj?.id
                      }
                    >
                      <div className="completed-vehicle-card">
                        <img
                          src={TruckXs}
                          alt="truck"
                          className="completed-vehicle-image"
                        />
                        <div className="complete-vehicle-code">
                          <span className="text-sm text-semi-bold">
                            {vehicleObj?.vehicle_tracking_code}
                          </span>
                        </div>

                        <div className="completed-vehicle-card-container div-flex-column rg-10">
                          <span className="text-lg text-bold mt-4">
                            {vehicleObj?.vehicle_number}
                          </span>
                          <Row className="g-1">
                            <Col xxl={6} xl={6} lg={6} md={6} sm={6} xs={6}>
                              <div className="div-flex-column">
                                <span className="text-sm">PO Quantity</span>
                                <span className="text-sm text-grey">
                                  {vehicleObj?.total_po_qty ?? ''}
                                </span>
                              </div>
                            </Col>

                            <Col xxl={6} xl={6} lg={6} md={6} sm={6} xs={6}>
                              <div className="div-flex-column">
                                <span className="text-sm">Current Station</span>
                                <span className="text-sm text-grey">
                                  {vehicleObj?.workflow_stage_name ?? ''}
                                </span>
                              </div>
                            </Col>
                          </Row>
                        </div>

                        <div className="complete-vehicle-view-action">
                          <div
                            className={`theme-background-${themeColor} div-flex-center py-1`}
                            onClick={() =>
                              navigate(
                                `/${selectedWorkflowStageId || 1}/${vehicleObj?.vehicle_tracking_number}`,
                              )
                            }
                            tabIndex={0}
                            role="button"
                          >
                            <span className="text-md text-semi-bold text-white">
                              View more →
                            </span>
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </div>
              ) : (
                <div className="div-flex-column rg-10 completed-vehicle-card div-flex-center div-align-center">
                  <CompletedVehicles />
                  <span className="text-sm text-grey">
                    No vehicle in vehicle tracking completed stage.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OverallDashboard;
